import { ParsedUrlQuery } from 'querystring'

import { DEFAULT_FEDERAL_CONTRACT_FILTER } from '@/constants/search/federal/contract'

import { isValidDate } from '@/helpers/date'

import type { DibbsContractSearchFilter } from '@/types/dibbs/Dibbs'
import type { FederalContractSearchFilter } from '@/types/federal-contract/Filter'
import type { FederalGrantSearchFilter } from '@/types/federal-grant/Filter'
import type { Status } from '@/types/federal-grant/Status'
import type { OpportunityType } from '@/types/Opportunity'
import type { Organization } from '@/types/Organization'
import type { SbirSearchFilter } from '@/types/sbir/Filter'
import type { SearchRequest } from '@/types/Search'
import type { SledContractSearchFilter } from '@/types/sled-contract/Filter'

export const buildQueryString = (
  request: SearchRequest,
  includePaginationParameters: boolean = true,
): string => {
  const params = new URLSearchParams()

  const appendIfPresent = (key: string, value?: any) => {
    if (value !== undefined && value !== null) {
      if (Array.isArray(value)) {
        value.forEach((val) => params.append(key, val.toString()))
      } else if (value instanceof Date) {
        params.append(key, value.toISOString())
      } else if (typeof value === 'boolean') {
        params.append(key, String(value))
      } else {
        params.append(key, value.toString())
      }
    }
  }

  // pagination parameters
  appendIfPresent('query', request.query)

  if (request.use_fts) {
    appendIfPresent('use_fts', request.use_fts)
  }

  if (includePaginationParameters) {
    appendIfPresent('limit', request.limit)
    appendIfPresent('offset', request.offset)
  }

  // org_id
  appendIfPresent('organization_id', request.organization_id)

  // filters
  const filter = request.filter.filter
  if (filter) {
    appendIfPresent('is_active', filter.is_active)
    appendIfPresent('active_after', filter.active_after)
    appendIfPresent('active_before', filter.active_before)
    appendIfPresent('published_after', filter.published_after)
    appendIfPresent('published_before', filter.published_before)
  }
  // handle array parameters
  if (request.filter.type === 'dibbs_contract') {
    const filter = request.filter.filter
    // TODO: add other dibbs filters
    ;[
      'nsn_numbers',
      'psc_codes',
      'set_aside_codes',
      'cage_codes',
      'part_numbers',
    ].forEach((key) =>
      appendIfPresent(key, filter[key as keyof DibbsContractSearchFilter]),
    )
  } else if (request.filter.type === 'federal_contract') {
    const filter = request.filter.filter
    ;[
      'buyer_ids',
      'naics_codes',
      'notice_types',
      'psc_codes',
      'set_aside_codes',
      'solicitation_numbers',
    ].forEach((key) =>
      appendIfPresent(key, filter[key as keyof FederalContractSearchFilter]),
    )
  } else if (request.filter.type === 'federal_grant') {
    const filter = request.filter.filter
    ;[
      'agencies',
      'eligible_applicants',
      'funding_categories',
      'funding_instruments',
      'opportunity_categories',
      'statuses',
    ].forEach((key) =>
      appendIfPresent(key, filter[key as keyof FederalGrantSearchFilter]),
    )
  } else if (request.filter.type === 'sbir') {
    const filter = request.filter.filter
    ;[
      'numbers',
      'programs',
      'phases',
      'agencies',
      'branches',
      'current_statuses',
      'topic_branches',
      'topic_numbers',
    ].forEach((key) =>
      appendIfPresent(key, filter[key as keyof SbirSearchFilter]),
    )
  } else if (request.filter.type === 'sled_contract') {
    const filter = request.filter.filter
    ;['jurisdiction_ids', 'nigp_codes', 'unspsc_codes'].forEach((key) =>
      appendIfPresent(key, filter[key as keyof SledContractSearchFilter]),
    )
  }

  const paramString = params.toString()
  if (paramString === '') {
    return ''
  }
  return `?${params.toString()}`
}

export const buildSearchUrl = (
  request: SearchRequest,
  includePaginationParameters: boolean = true,
) => {
  const queryString = buildQueryString(request, includePaginationParameters)
  switch (request.type) {
    case 'dibbs_contract':
      return `/dibbs-contracts/search${queryString}`
    case 'federal_contract':
      return `/federal-contracts/search${queryString}`
    case 'federal_grant':
      return `/federal-grants/search${queryString}`
    case 'sbir':
      return `/sbirs/search${queryString}`
    case 'sled_contract':
      return `/sled-contracts/search${queryString}`
    default:
      const exhaustiveCheck: never = request.type
      throw new Error(`Unknown opportunity type: ${request.type}`)
  }
}

export const buildOpportunityUrl = (id: string, type: OpportunityType) => {
  switch (type) {
    case 'dibbs_contract':
      return `/dibbs-contracts/${id}`
    case 'federal_contract':
      return `/federal-contracts/${id}`
    case 'federal_grant':
      return `/federal-grants/${id}`
    case 'sbir':
      return `/sbirs/${id}`
    case 'sled_contract':
      return `/sled-contracts/${id}`
    default:
      const exhaustiveCheck: never = type
      throw new Error(`Unknown opportunity type: ${type}`)
  }
}

export const buildSearchUrlFromQuery = (
  query: string,
  type: OpportunityType,
  raiseError: boolean = true,
): string => {
  const encodedQuery = encodeURIComponent(query)
  switch (type) {
    case 'dibbs_contract':
      return `/dibbs-contracts/search?query=${encodedQuery}`
    case 'federal_contract':
      return `/federal-contracts/search?query=${encodedQuery}`
    case 'federal_grant':
      return `/federal-grants/search?query=${encodedQuery}`
    case 'sbir':
      return `/sbirs/search?query=${encodedQuery}`
    case 'sled_contract':
      return `/sled-contracts/search?query=${encodedQuery}`
    default:
      const exhaustiveCheck: never = type
      if (raiseError) {
        throw new Error(`Unknown opportunity type: ${type}`)
      }
      return `/federal-contracts/search?query=${encodedQuery}`
  }
}

export const buildExportUrl = (request: SearchRequest) => {
  const queryString = buildQueryString(request, false)
  switch (request.type) {
    case 'dibbs_contract':
      return `/dibbs-contracts/search/export${queryString}`
    case 'federal_contract':
      return `/federal-contracts/search/export${queryString}`
    case 'federal_grant':
      return `/federal-grants/search/export${queryString}`
    case 'sbir':
      return `/sbirs/search/export${queryString}`
    case 'sled_contract':
      return `/sled-contracts/search/export${queryString}`
    default:
      const exhaustiveCheck: never = request.type
      throw new Error(`Unknown opportunity type: ${request.type}`)
  }
}

export const parseQueryString = (
  query: ParsedUrlQuery,
  type: OpportunityType,
): SearchRequest => {
  const request: Partial<SearchRequest> = {}

  const getSingleParam = (name: string): string | undefined => {
    const param = query[name]
    return typeof param === 'string' ? param : undefined
  }

  const getArrayParam = (name: string): string[] => {
    const param = query[name]
    if (typeof param === 'string') {
      return [param]
    }
    return param ?? []
  }

  request.query = getSingleParam('query') ?? ''
  request.organization_id = getSingleParam('organization_id')

  const limit = getSingleParam('limit')
  request.limit = limit ? parseInt(limit) : undefined
  const offset = getSingleParam('offset')
  request.offset = offset ? parseInt(offset) : undefined

  const active_after = getSingleParam('active_after')
  const active_before = getSingleParam('active_before')
  const published_after = getSingleParam('published_after')
  const published_before = getSingleParam('published_before')
  const is_active = getSingleParam('is_active')

  // Determine the type of filter based on the URL path
  if (type === 'dibbs_contract') {
    request.type = 'dibbs_contract'
    const dibbsFilter: DibbsContractSearchFilter = {
      // TODO: add other dibbs filters
      nsn_numbers: getArrayParam('nsn_numbers'),
      psc_codes: getArrayParam('psc_codes'),
      set_aside_codes: getArrayParam('set_aside_codes'),
      cage_codes: getArrayParam('cage_codes'),
      part_numbers: getArrayParam('part_numbers'),
      is_active: is_active !== undefined ? is_active === 'true' : null,
      active_after: isValidDate(active_after)
        ? active_after
          ? active_after
          : null
        : null,
      active_before: isValidDate(active_before)
        ? active_before
          ? active_before
          : null
        : null,
      published_after: isValidDate(published_after)
        ? published_after
          ? published_after
          : null
        : null,
      published_before: isValidDate(published_before)
        ? published_before
          ? published_before
          : null
        : null,
    }
    request.filter = {
      type: 'dibbs_contract',
      filter: dibbsFilter,
    }
  } else if (type === 'federal_contract') {
    request.type = 'federal_contract'
    const noticeTypesFromUrl = getArrayParam('notice_types')
    const contractFilter: FederalContractSearchFilter = {
      buyer_ids: getArrayParam('buyer_ids'),
      // Default to all notice types if none specified in URL
      notice_types:
        noticeTypesFromUrl.length > 0
          ? noticeTypesFromUrl
          : DEFAULT_FEDERAL_CONTRACT_FILTER.notice_types,
      naics_codes: getArrayParam('naics_codes'),
      psc_codes: getArrayParam('psc_codes'),
      set_aside_codes: getArrayParam('set_aside_codes'),
      solicitation_numbers: getArrayParam('solicitation_numbers'),
      is_active: is_active !== undefined ? is_active === 'true' : null,
      active_after: isValidDate(active_after)
        ? active_after
          ? active_after
          : null
        : null,
      active_before: isValidDate(active_before)
        ? active_before
          ? active_before
          : null
        : null,
      published_after: isValidDate(published_after)
        ? published_after
          ? published_after
          : null
        : null,
      published_before: isValidDate(published_before)
        ? published_before
          ? published_before
          : null
        : null,
    }
    request.filter = {
      type: 'federal_contract',
      filter: contractFilter,
    }
  } else if (type === 'federal_grant') {
    request.type = 'federal_grant'
    const statuses: Array<Status> = getArrayParam('statuses') as Status[]
    const grantFilter: FederalGrantSearchFilter = {
      agencies: getArrayParam('agencies'),
      eligible_applicants: getArrayParam('eligible_applicants'),
      funding_categories: getArrayParam('funding_categories'),
      funding_instruments: getArrayParam('funding_instruments'),
      opportunity_categories: getArrayParam('opportunity_categories'),
      statuses: statuses,
      is_active: is_active !== undefined ? is_active === 'true' : null,
      active_after: isValidDate(active_after)
        ? active_after
          ? active_after
          : null
        : null,
      active_before: isValidDate(active_before)
        ? active_before
          ? active_before
          : null
        : null,
      published_after: isValidDate(published_after)
        ? published_after
          ? published_after
          : null
        : null,
      published_before: isValidDate(published_before)
        ? published_before
          ? published_before
          : null
        : null,
    }
    request.filter = {
      type: 'federal_grant',
      filter: grantFilter,
    }
  } else if (type === 'sbir') {
    request.type = 'sbir'
    const sbirFilter: SbirSearchFilter = {
      numbers: getArrayParam('numbers'),
      programs: getArrayParam('programs'),
      phases: getArrayParam('phases'),
      agencies: getArrayParam('agencies'),
      branches: getArrayParam('branches'),
      current_statuses: getArrayParam('current_statuses'),
      topic_branches: getArrayParam('topic_branches'),
      topic_numbers: getArrayParam('topic_numbers'),
      is_active: is_active !== undefined ? is_active === 'true' : null,
      active_after: isValidDate(active_after)
        ? active_after
          ? active_after
          : null
        : null,
      active_before: isValidDate(active_before)
        ? active_before
          ? active_before
          : null
        : null,
      published_after: isValidDate(published_after)
        ? published_after
          ? published_after
          : null
        : null,
      published_before: isValidDate(published_before)
        ? published_before
          ? published_before
          : null
        : null,
    }
    request.filter = {
      type: 'sbir',
      filter: sbirFilter,
    }
  } else if (type === 'sled_contract') {
    request.type = 'sled_contract'
    const sledContractFilter: SledContractSearchFilter = {
      jurisdiction_ids: getArrayParam('jurisdiction_ids'),
      nigp_codes: getArrayParam('nigp_codes'),
      unspsc_codes: getArrayParam('unspsc_codes'),
      is_active: is_active !== undefined ? is_active === 'true' : null,
      active_after: isValidDate(active_after)
        ? active_after
          ? active_after
          : null
        : null,
      active_before: isValidDate(active_before)
        ? active_before
          ? active_before
          : null
        : null,
      published_after: isValidDate(published_after)
        ? published_after
          ? published_after
          : null
        : null,
      published_before: isValidDate(published_before)
        ? published_before
          ? published_before
          : null
        : null,
    }
    request.filter = {
      type: 'sled_contract',
      filter: sledContractFilter,
    }
  }

  return request as SearchRequest
}

export const buildDefaultOrgSearchUrl = (
  organization: Organization,
  raiseError: boolean = true,
) => {
  let query = ''
  if (organization.website_url && organization.website_url.length > 0) {
    query = organization.website_url
  } else if (organization.description && organization.description.length > 0) {
    query = organization.description.slice(0, 256)
  }
  const url = buildSearchUrlFromQuery(
    query,
    organization.default_opportunity_type,
    raiseError,
  )
  return url
}
